import React, { ReactNode } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { RoomFormExportConfiguration } from './RoomFormExportConfiguration';
import { OrderSearchOptions } from '@/services/orders';
import { SimpleExportConfiguration } from './SimpleExportConfiguration';
import {
    getCostsBreakdownExportUrl,
    getInvoiceExportUrl,
    getOrderRoomFormByUnitExportUrl,
    getOrderRoomFormExportUrl,
    getOrderSimpleExportUrl,
    getUniziteOrderExport,
    InvoiceExportOptions,
    RoomFormByUnitExportOptions,
    RoomFormExportOptions,
    SimpleOrderExportOptions,
} from '@/services/order-export';
import { Project } from '@/types';
import { InvoiceExportConfiguration } from './InvoiceExportConfiguration';
import { RoomFormByUnitExportConfiguration } from './RoomFormByUnitExportConfiguration';

interface RenderOptions<T> {
    value: T;
    onChange(value: T): void;
}

export type OrderExportTypeKey = 'simple' | 'room-form' | 'room-form-by-unit' | 'invoice' | 'costs' | 'unizite';
export interface OrderExportType<T> {
    key: OrderExportTypeKey;
    name(): string;
    render(options: RenderOptions<T>): ReactNode;
    exportUrl(filter: OrderSearchOptions, options: T): string;
    visible?(project: Project): boolean;
}

export const OrderExportTypes: OrderExportType<any>[] = [];
function addExportType<T>(exportType: OrderExportType<T>) {
    OrderExportTypes.push(exportType);
}

addExportType<SimpleOrderExportOptions>({
    key: 'simple',
    name: () => t`Simple export`,
    render: (options) => <SimpleExportConfiguration {...options} />,
    exportUrl: (filter, options) => getOrderSimpleExportUrl(filter, options),
});

addExportType<RoomFormExportOptions>({
    key: 'room-form',
    name: () => t`Room form`,
    render: (options) => <RoomFormExportConfiguration {...options} />,
    exportUrl: (filter, options) => getOrderRoomFormExportUrl(filter, options),
});

addExportType<RoomFormByUnitExportOptions>({
    key: 'room-form-by-unit',
    name: () => t`Room form by unit`,
    render: (options) => <RoomFormByUnitExportConfiguration {...options} />,
    exportUrl: (filter, options) => getOrderRoomFormByUnitExportUrl(filter, options),
});

addExportType<InvoiceExportOptions>({
    key: 'invoice',
    name: () => t`Invoice`,
    render: (options) => <InvoiceExportConfiguration {...options} />,
    exportUrl: (filter, options) => getInvoiceExportUrl(filter, options),
    visible: (project) => project.permissions.canViewSensitiveData,
});

addExportType<InvoiceExportOptions>({
    key: 'costs',
    name: () => t`Costs overview`,
    render: (options) => <InvoiceExportConfiguration {...options} />,
    exportUrl: (filter, options) => getCostsBreakdownExportUrl(filter, options),
    visible: (project) => project.permissions.canViewSensitiveData,
});

addExportType<{}>({
    key: 'unizite',
    name: () => t`UniZite`,
    render: (options) => <Trans>This export cannot be changed</Trans>,
    exportUrl: (filter, options) => getUniziteOrderExport(filter),
    visible: () => window.APP_CONFIG.ENVIRONMENT !== 'Production',
});
